import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { getApiUrl } from "../../config/api";
import CustomNavbar from "../CustomPermissions/CustomNavbar";
import LoadingSpinner from "../common/LoadingSpinner"; // Assuming this exists or I'll generic load
import ErrorMessage from "../common/ErrorMessage"; // Assuming this exists

const MomView = () => {
    const { id } = useParams();
    const [momData, setMomData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMomDetails = async () => {
            try {
                const response = await axios.get(getApiUrl(`/api/moms/${id}/`), {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                });
                setMomData(response.data);
            } catch (err) {
                console.error("Error fetching MOM details:", err);
                setError("Failed to load Minutes of Meeting details.");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchMomDetails();
        }
    }, [id]);

    const cleanHTML = (html) => {
        return { __html: DOMPurify.sanitize(html) };
    };

    const formatDateTime = (datetimeString) => {
        if (!datetimeString) return "";
        const options = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(datetimeString).toLocaleString(undefined, options);
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50">
                <div className="text-xl text-blue-600 font-semibold animate-pulse">Loading Document...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center bg-red-50">
                <div className="text-xl text-red-600 font-bold">{error}</div>
            </div>
        );
    }

    if (!momData) {
        return null;
    }

    return (
        <div className="flex min-h-screen bg-gray-100">
            {/* Optional: Include Navbar if needed, or keep it standalone for print-friendliness */}
            {/* <CustomNavbar /> */}
            {/* User asked for "View in new page", usually implies a standalone document view. 
          I will stick to a clean document layout. */}

            <div className="container mx-auto px-4 py-8 max-w-4xl">
                <div className="bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white text-center md:text-left">
                        <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">{momData.title}</h1>
                        <div className="flex flex-col md:flex-row items-center text-blue-100 text-sm">
                            <div className="flex items-center mb-1 md:mb-0">
                                <span className="mr-2">📅</span>
                                <span className="font-medium text-dark opacity-90">{formatDateTime(momData.momdate)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Content Body */}
                    <div className="p-4 md:p-8">
                        <div className="prose prose-lg max-w-none text-gray-800">
                            {/* Style for CKEditor content to make tables scrollable */}
                            <style>{`
                .ck-content table {
                  width: 100%;
                  display: block;
                  overflow-x: auto;
                  white-space: nowrap;
                }
                .ck-content img {
                  max-width: 100%;
                  height: auto;
                }
                @media (min-width: 768px) {
                  .ck-content table {
                    display: table;
                    white-space: normal;
                  }
                  /* If table is still too wide, force scroll */
                  .bg-white {
                     overflow-x: hidden; 
                  }
                }
              `}</style>

                            <div
                                dangerouslySetInnerHTML={cleanHTML(momData.summary)}
                                className="ck-content overflow-x-auto"
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 border-t border-gray-200 p-4 text-center text-gray-500 text-sm">
                        Docladder System • Minutes of Meeting Record • ID: {momData.id}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MomView;
